import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import SettingCard from '../../settings/SettingCard';
import styles from './InvitationSettings.module.scss';
import { gql } from '../../graphql/generated/gql';

const UPDATE_ORGANIZATION_INVITE_SETTING_MUTATION = gql(`
  mutation updateOrganizationInviteSetting($requireInviteApproval: Boolean!) {
    organization: updateOrganization(
      settings: { requireInviteApproval: $requireInviteApproval }
    ) {
      id
      settings {
        requireInviteApproval
      }
    }
  }
`);

const REQUIRE_INVITE_APPROVAL_SETTINGS_QUERY = gql(`
  query requireInviteApprovalSettings {
    session: currentSession {
      organization {
        id
        settings {
          requireInviteApproval
        }
      }
    }
  }
`);

const OPTIONS = {
  enabled: 'Invitations must be approved by an admin',
  disabled: 'Users can directly invite new teammates',
} as const;

type InviteApprovalValue = keyof typeof OPTIONS;

const InvitationSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [updateSettings] = useMutation(
    UPDATE_ORGANIZATION_INVITE_SETTING_MUTATION,
  );

  const { data } = useQuery(REQUIRE_INVITE_APPROVAL_SETTINGS_QUERY);
  if (!data) return null;

  const currentValue = data.session.organization.settings?.requireInviteApproval
    ? 'enabled'
    : 'disabled';

  const handleSubmit = async ({
    requireInviteApproval,
  }: {
    requireInviteApproval: InviteApprovalValue;
  }) => {
    await updateSettings({
      variables: {
        requireInviteApproval: requireInviteApproval === 'enabled',
      },
    });
    setIsEditing(false);
  };

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Invitations"
          description={
            <Form<{ requireInviteApproval: InviteApprovalValue }>
              initialValues={{ requireInviteApproval: currentValue }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(['disabled', 'enabled'] as const).map((value) => (
                  <Field
                    key={value}
                    label={OPTIONS[value]}
                    name="requireInviteApproval"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            <Button onClick={() => setIsEditing(true)} variant="secondary">
              Edit
            </Button>
          }
          title="Invitations"
          description={OPTIONS[currentValue]}
        />
      )}
    </>
  );
};

export default InvitationSettings;
